import Cookies from "js-cookie"
import type { AppDispatch, RootState } from "redux/store";

import { KeyCloakConfig } from "api/config";
import { authorizationService, userService } from "api";
import { parseJwt } from "utils/KeykloackHelper";
import { savedUserAccountsHandler, removeUserDtoFormStorage, removeUserSavedAccounts } from "utils/LocalStorageHelper";
import { informationRequestError } from "constants/errors";
import { setThemeSet, setDarkMode, THEME_SETS } from 'redux/theme/reducer';
import { setTemporaryNotifications } from "redux/notifications/actions";
import { defaultNotificationCreator } from "templates/Notifications";
import {
    SET_USER_REGISTARTION_STEPS,
    SET_USER_SAVED_ACCOUNTS,
    CLEAR_CURRENT_USER_DATA,
    SET_USER_CURATOR_DATA,
    SET_USER_CURATOR_DATA_LOADED,
    SET_USER_INFO,
    SET_CURRENT_USER,
    SET_USER_CURRENCY,
    SET_USER_SETTINGS,
    UPDATE_USER_SETTINGS,
    defaultSettings,
} from "./constants";
import type { IRefresh, ISetCurrentUser, ISavedAccounts, IUserRegistrationStepsArray, IUserCurator, IUserInfo, IUserSettings } from "./types";
import { setShortcuts } from "redux/shortcuts/reducer";

export const refresh = (data: IRefresh) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
        const response = await authorizationService.refresh(data);
        const authData = await response.json();

        const currentUserId = getState().users.currentUser.data.id;

        if (response.status > 300) {
            throw new Error(`Authorization error: ${authData && authData.description}`)
        }

        if(currentUserId && currentUserId !== data.id) {
            dispatch(clearCurrentUserData());
        }

        if (response.status < 300) {
            dispatch(setUserRefreshData(authData) as any)
        } else {
            throw new Error(authData.error + ': ' + authData.error_description)
        }
    } catch (error: any) {
        console.error(error.message);
        removeUserSavedAccounts(data);
        const refreshAccountIsCurrentUser = getState().users.currentUser.data.id === data.id;
        if(refreshAccountIsCurrentUser) {
            dispatch(clearCurrentUserData());
            removeUserDtoFormStorage()
        }
        window.location.href = `${process.env.REACT_APP_AUTH_APP_ADD_ACCOUNT_URL}`;
    }
}

export const setUserRefreshData = (authData: any) => (dispatch: AppDispatch) => {
    const refreshToken = authData.refresh_token,
                accessToken = authData.access_token,
                JWTData = parseJwt(accessToken);

    if (JWTData) {
        const cookie = Cookies.withAttributes({...KeyCloakConfig.refresh, domain: process.env.REACT_APP_COOKIE_DOMAIN,});
        cookie.set("jwt_access", accessToken);
        cookie.set("jwt_refresh", refreshToken);
        cookie.set("currentUserId", JWTData.sub);
        
        savedUserAccountsHandler({
            id: JWTData.sub,
            refreshToken: refreshToken,
            accessToken: accessToken,
            email: JWTData.email
        })
        
        dispatch(setCurrentUser({
            userData: { 
                id: JWTData.sub, 
                email: JWTData.email, 
                name: JWTData.name && JWTData.name, 
                roles: JWTData.resource_access.account.roles, 
                realm_access_roles: JWTData.realm_access.roles,
                accessToken: accessToken, 
                avatar: null 
            },
            savedAccounts: JSON.parse(localStorage.getItem("savedAccounts") || "[]")
        }))

    } else {
        throw new Error('Error: Authorization error, please try again.')
    }
}

export const setCurrentUser = (data: ISetCurrentUser) => ({
    type: SET_CURRENT_USER,
    payload: data
})

export const setUserCurrency = (data: string) => ({
    type: SET_USER_CURRENCY,
    payload: data
})

export const setUserSavedAccounts = (data: Array<ISavedAccounts>) => ({
    type: SET_USER_SAVED_ACCOUNTS,
    payload: data
})

export const removeAccount = (id: string) => (dispatch: AppDispatch) => {
    const updated = removeUserSavedAccounts({ id });
    dispatch(setUserSavedAccounts(updated ?? []));
};

// LOGOUT

export const logout = (id: any) => (dispatch: AppDispatch) => {
    removeUserDtoFormStorage();
    const savedAccounts = removeUserSavedAccounts(id);
    dispatch(setUserSavedAccounts(savedAccounts));
    dispatch(clearCurrentUserData());
    window.location.href = `${process.env.REACT_APP_AUTH_APP_LOGIN_URL}`;
}


// Registration steps

export const getUserRegistrationSteps = (id: string) => async (dispatch: AppDispatch) => {
    try {
        const response = await userService.getUserRegistrationSteps(id);

        if (response && response.data && response.data?.data) {
            dispatch(setUserRegistrationSteps(response.data.data))
        } else {
            throw new Error("При получении шагов регистрации произошла ошибка")
        }

    } catch (error: any) {
        console.error(error.message);
    }
}

export const setUserRegistrationSteps = (data: IUserRegistrationStepsArray) => ({
    type: SET_USER_REGISTARTION_STEPS,
    payload: data
})

// Billing

export const getUserInfo = () => async (dispatch: AppDispatch) => {
    try {
        const response = await userService.getUserInfo();

        if (response && response.status < 300 && response.data && response.data) {
            dispatch(setUserInfo(response.data.data))
        } else {
            throw new Error(informationRequestError)
        }

    } catch (error: any) {
        console.error(error);
        dispatch(setTemporaryNotifications(
            defaultNotificationCreator({
                title: informationRequestError,
                description: error.message,
                iconType: "warning"
            })
        ))
    }
}

export const setUserInfo = (data: IUserInfo | null) => ({
    type: SET_USER_INFO,
    payload: data
})

export const setUserSettings = (data: IUserSettings) => ({
    type: SET_USER_SETTINGS,
    payload: data
});

export const getUserSettings = () => async (dispatch: AppDispatch) => {
    try {
        const response = await userService.getUserSettings();

        if (response && response.status < 300 && response.data) {
            const settings = response.data.data || defaultSettings;

            dispatch(setUserSettings(settings));

            if (settings.colorSet) {
                const themeSet = Object.values(THEME_SETS).find(
                    theme => theme.id === settings.colorSet
                );
                if (themeSet) {
                    dispatch(setThemeSet(themeSet));
                }
            }
            if (settings.shortcuts) {
                dispatch(setShortcuts(settings.shortcuts));
            }

            if (settings.currency) {
                dispatch(setUserCurrency(settings.currency));
            }

            if (settings.theme === 'dark') {
                dispatch(setDarkMode(true));
            } else if (settings.theme === 'light') {
                dispatch(setDarkMode(false));
            }
        } else {
            throw new Error(informationRequestError);
        }

    } catch (error: any) {
        console.error(error);
        dispatch(setUserSettings(defaultSettings));
        dispatch(setTemporaryNotifications(
            defaultNotificationCreator({
                title: informationRequestError,
                description: error.message,
                iconType: "warning"
            })
        ));
    }
};

export const updateUserSettings = (settings: Partial<IUserSettings>, silent = false) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const currentSettings = getState().users.currentUser.settings;
    try {
        const newSettings = { ...currentSettings, ...settings };

        dispatch({ type: UPDATE_USER_SETTINGS, payload: newSettings });

        const response = await userService.postUserSettings(newSettings);

        if (response?.status < 300) {
            if (!silent) {
                dispatch(setTemporaryNotifications(
                    defaultNotificationCreator({
                        title: "Настройки сохранены",
                        description: "",
                        iconType: "success"
                    })
                ));
            }
        } else {
            throw new Error("Ошибка сервера");
        }

    } catch (error: any) {
        console.error(error);

        // Откатываем оптимистичный апдейт, чтобы redux не расходился с сервером
        dispatch({ type: UPDATE_USER_SETTINGS, payload: currentSettings });
        if (settings.colorSet && settings.colorSet !== currentSettings?.colorSet) {
            const prevThemeSet = Object.values(THEME_SETS).find(t => t.id === currentSettings?.colorSet);
            if (prevThemeSet) dispatch(setThemeSet(prevThemeSet));
        }

        dispatch(setTemporaryNotifications(
            defaultNotificationCreator({
                title: "Ошибка сохранения",
                description: error.message || "Не удалось сохранить настройки",
                iconType: "error"
            })
        ));
    }
};

// curator

export const getUserCuratorInfo = () => async (dispatch: AppDispatch) => {
    try {
        const response = await userService.getUserCuratorInfo();

        if (response && response.status < 300) {
            dispatch(setUserCuratorData(response.data.data))
        } else {
            throw new Error(informationRequestError)
        }

    } catch (error: any) {
        console.error(error)
        dispatch(setUserCuratorDataLoaded(true));
        dispatch(setTemporaryNotifications(
            defaultNotificationCreator({
                title: informationRequestError,
                description: error.message,
                iconType: "warning"
            })
        ))
    }
}

const setUserCuratorDataLoaded = (data: boolean) => ({
    type: SET_USER_CURATOR_DATA_LOADED,
    payload: data
})

const setUserCuratorData = (data: IUserCurator) => ({
    type: SET_USER_CURATOR_DATA,
    payload: data
})

// Clear user data

export const clearCurrentUserData = () => ({
    type: CLEAR_CURRENT_USER_DATA
})
